import React from "react";

import pdf from "@react-pdf/renderer";

import Macronutrients from "@/components/MealPlanForm/store/Macronutrients";
import { Features } from "@/config/features";
import { graphql } from "@/gql";
import { Maybe } from "@/gql/graphql";
import { colors } from "@/theme/overrides/colors";
import { fraction, round } from "@/utils/number";
import { FragmentType } from "@/utils/type";

import Divider from "./Divider";
import Flex from "./layout/Flex";

const { Text, View, StyleSheet } = pdf;

const styles = StyleSheet.create({
  root: {
    width: "100%",
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginTop: "12px",
  },
  mealPlan: {
    fontWeight: "extrabold",
    fontSize: "20px",
  },
  cal: {
    fontWeight: "semibold",
    fontSize: "14px",
  },
  textSecondary: {
    color: colors.grey,
  },
  bold: {
    fontWeight: "bold",
  },
  caption: {
    fontSize: "10px",
  },
});

type Props = {
  data?: Maybe<FragmentType<typeof contentHeaderFragment>>;
  totalCalories: number;
};

const contentHeaderFragment = graphql(`
  fragment PDFMealPlanContentHeader on MealPlan {
    id
    macronutrientsCarbohydratePercent
    macronutrientsProteinPercent
    macronutrientsFatPercent
  }
`);

const ContentHeader: React.FC<Props> = ({ data, totalCalories }) => {
  const proteinGrams = Macronutrients.macronutrientGrams(
    "proteins",
    fraction(data?.macronutrientsProteinPercent ?? 0),
    totalCalories
  );
  const fatGrams = Macronutrients.macronutrientGrams(
    "fats",
    fraction(data?.macronutrientsFatPercent ?? 0),
    totalCalories
  );
  const carbohydrateGrams = Macronutrients.macronutrientGrams(
    "carbohydrates",
    fraction(data?.macronutrientsCarbohydratePercent ?? 0),
    totalCalories
  );

  return (
    <View wrap={false}>
      <View style={styles.root}>
        <Flex alignItems="center">
          <Text style={styles.mealPlan}>Meal Plan</Text>
          <View style={{ marginLeft: "8px" }} />
          <Text style={styles.cal}>{`${Math.round(totalCalories)} kcal`}</Text>
        </Flex>

        {Features.pdfMacronutrientsEnabled && (
          <View style={{ width: "100%" }}>
            <Flex flexDirection="row" justifyContent="spaceBetween">
              <Flex flexDirection="row" alignItems="center">
                <Text style={[styles.bold, styles.caption]}>P:</Text>
                <View style={{ marginRight: "2px" }} />
                <Text
                  style={[styles.bold, styles.caption, styles.textSecondary]}
                >
                  {`${data?.macronutrientsProteinPercent ?? 0}%`}
                  {` (${round(proteinGrams, 2)}g)`}
                </Text>
              </Flex>
              <Flex
                flexDirection="row"
                alignItems="center"
                justifyContent="center"
              >
                <Text style={[styles.bold, styles.caption]}>F:</Text>
                <View style={{ marginRight: "2px" }} />
                <Text
                  style={[styles.bold, styles.caption, styles.textSecondary]}
                >
                  {`${data?.macronutrientsFatPercent ?? 0}%`}
                  {` (${round(fatGrams, 2)}g)`}
                </Text>
              </Flex>
              <Flex flexDirection="row" alignItems="center">
                <Text style={[styles.bold, styles.caption]}>C:</Text>
                <View style={{ marginRight: "2px" }} />
                <Text
                  style={[styles.bold, styles.caption, styles.textSecondary]}
                >
                  {`${data?.macronutrientsCarbohydratePercent ?? 0}%`}
                  {` (${round(carbohydrateGrams, 2)}g)`}
                </Text>
              </Flex>
            </Flex>
          </View>
        )}
      </View>
      <View style={{ marginBottom: "8px" }} />
      <Divider size="large" />
    </View>
  );
};
export default ContentHeader;
